import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useMe } from './api/hooks.js';
import { canAccessProjects } from './auth/access.js';
import { Callback } from './auth/Callback.js';
import { useAuth } from './auth/AuthContext.js';
import { AdminPage } from './pages/admin/AdminPage.js';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage.js';
import { Approvals } from './pages/Approvals.js';
import { HelpPage } from './pages/HelpPage.js';
import { Home } from './pages/Home.js';
import { ProjectsPage } from './pages/projects/ProjectsPage.js';
import { isNotProvisioned, NotProvisioned } from './pages/NotProvisioned.js';
import { Timesheet } from './pages/Timesheet.js';

// Every authenticated route runs this check. Without it an unprovisioned federated user —
// authenticated by Enterprise Auth but rejected by the API — bounces to "/", which sees a
// valid session and redirects straight back, so they get a loop instead of an explanation.
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const meQuery = useMe();

  if (isLoading || meQuery.isLoading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (isNotProvisioned(meQuery.error)) return <NotProvisioned />;
  return <>{children}</>;
}

function ApproverRoute({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const meQuery = useMe();

  if (authLoading || meQuery.isLoading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (isNotProvisioned(meQuery.error)) return <NotProvisioned />;
  if (meQuery.isError || meQuery.data?.role === 'contributor') return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Projects & tasks: approvers/admins, plus a non-approver lead (contributor who
// leads ≥1 project). Mirrors the nav's canAccessProjects so the link and the
// route never disagree.
function ProjectsRoute({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const meQuery = useMe();

  if (authLoading || meQuery.isLoading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (isNotProvisioned(meQuery.error)) return <NotProvisioned />;
  if (meQuery.isError || !canAccessProjects(meQuery.data)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const meQuery = useMe();

  if (authLoading || meQuery.isLoading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (isNotProvisioned(meQuery.error)) return <NotProvisioned />;
  if (meQuery.isError || meQuery.data?.role !== 'administrator') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function Router() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/auth/callback" element={<Callback />} />
      <Route
        path="/timesheet"
        element={
          <ProtectedRoute>
            <Timesheet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <ApproverRoute>
            <Approvals />
          </ApproverRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ApproverRoute>
            <AnalyticsPage />
          </ApproverRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProjectsRoute>
            <ProjectsPage />
          </ProjectsRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin">
        <Route
          index
          element={
            <AdminRoute>
              <Navigate to="teams" replace />
            </AdminRoute>
          }
        />
        <Route
          path=":tab"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
      </Route>
    </Routes>
  );
}
